import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react'
import { ChevronRightIcon, XCloseIcon } from './icons'

type ButtonVariant = 'primary' | 'secondary' | 'ghost'
type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  children: ReactNode
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  fullWidth?: boolean
}

const variantStyles: Record<ButtonVariant, string> = {
  // Primary: solid brand when enabled; muted solid teal when disabled (WCAG AA, no opacity wash)
  primary:
    'bg-background-brand-solid text-text-white shadow-xs disabled:bg-background-brand-solid-disabled disabled:text-text-white disabled:shadow-none',
  secondary:
    'bg-background-primary border border-border-primary border-solid text-text-secondary-700 shadow-xs disabled:opacity-50',
  ghost: 'bg-transparent text-text-secondary-700 disabled:opacity-50',
}

const disabledStyles = 'disabled:cursor-not-allowed'

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-[12px] py-[8px] text-sm leading-sm',
  md: 'px-[16px] py-[10px] text-md leading-md',
  lg: 'px-[18px] py-[12px] text-md leading-md',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', children, leftIcon, rightIcon, fullWidth, className = '', ...props }, ref) => {
    const baseStyles = 'font-body font-semibold flex gap-[6px] items-center justify-center overflow-clip rounded-md shrink-0'
    const widthStyles = fullWidth ? 'w-full' : ''

    return (
      <button
        ref={ref}
        className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${widthStyles} ${disabledStyles} ${className}`}
        {...props}
      >
        {leftIcon && <span className="overflow-clip relative shrink-0">{leftIcon}</span>}
        <span>{children}</span>
        {rightIcon && <span className="overflow-clip relative shrink-0">{rightIcon}</span>}
      </button>
    )
  }
)

Button.displayName = 'Button'

// Convenience components for common button patterns

interface PrimaryButtonProps extends Omit<ButtonProps, 'variant'> {}

export const PrimaryButton = forwardRef<HTMLButtonElement, PrimaryButtonProps>(
  (props, ref) => <Button ref={ref} variant="primary" {...props} />
)
PrimaryButton.displayName = 'PrimaryButton'

interface SecondaryButtonProps extends Omit<ButtonProps, 'variant'> {}

export const SecondaryButton = forwardRef<HTMLButtonElement, SecondaryButtonProps>(
  (props, ref) => <Button ref={ref} variant="secondary" {...props} />
)
SecondaryButton.displayName = 'SecondaryButton'

// Cancel button with X icon - small size for action bars
interface CancelButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  children?: ReactNode
}

export const CancelButton = forwardRef<HTMLButtonElement, CancelButtonProps>(
  ({ children = 'Cancel', className = '', ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={`bg-background-primary border border-border-primary border-solid flex gap-[4px] items-center justify-center overflow-clip px-[12px] py-[8px] relative rounded-md shadow-xs shrink-0 ${className}`}
        {...props}
      >
        <div className="overflow-clip relative shrink-0 size-5 text-text-tertiary-600">
          <XCloseIcon />
        </div>
        <span className="font-body font-semibold text-sm leading-sm text-text-secondary-700">
          {children}
        </span>
      </button>
    )
  }
)
CancelButton.displayName = 'CancelButton'

// Continue button with right chevron - small size for action bars
interface ContinueButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  children?: ReactNode
}

export const ContinueButton = forwardRef<HTMLButtonElement, ContinueButtonProps>(
  ({ children = 'Continue', className = '', ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={`bg-background-brand-solid flex gap-[4px] items-center justify-center overflow-clip px-[12px] py-[8px] relative rounded-md shadow-xs shrink-0 ${className}`}
        {...props}
      >
        <span className="font-body font-semibold text-sm leading-sm text-text-white">
          {children}
        </span>
        <div className="overflow-clip relative shrink-0 size-5 text-text-primary-on-brand">
          <ChevronRightIcon />
        </div>
      </button>
    )
  }
)
ContinueButton.displayName = 'ContinueButton'

// Toggle Button Group for Yes/No or multi-option selections
export interface ToggleOption {
  value: string
  label: string
}

interface ToggleButtonGroupProps {
  options: ToggleOption[]
  value?: string
  onChange: (value: string) => void
  className?: string
}

export function ToggleButtonGroup({ options, value, onChange, className = '' }: ToggleButtonGroupProps) {
  return (
    <div className={`border border-border-primary border-solid flex isolate items-start overflow-clip relative rounded-md shadow-xs shrink-0 w-full ${className}`}>
      {options.map((option, index) => {
        const isSelected = value === option.value
        const isLast = index === options.length - 1
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={`flex-1 flex gap-0 items-center justify-center min-h-[40px] px-[16px] py-[8px] relative shrink-0 ${
              !isLast ? 'border-r border-border-primary border-solid' : ''
            } ${
              isSelected ? 'bg-foreground-brand-primary-alt' : 'bg-background-primary'
            }`}
          >
            <p className={`flex-1 font-body font-semibold text-sm leading-sm text-center ${
              isSelected ? 'text-text-secondary-hover' : 'text-text-secondary-700'
            }`}>
              {option.label}
            </p>
          </button>
        )
      })}
    </div>
  )
}

// Convenience component for Yes/No toggles
interface YesNoToggleProps {
  value?: 'Yes' | 'No'
  onChange: (value: 'Yes' | 'No') => void
  className?: string
  disableNo?: boolean
}

export function YesNoToggle({ value, onChange, className = '', disableNo = false }: YesNoToggleProps) {
  return (
    <div className={`border border-border-primary border-solid flex isolate items-start overflow-clip relative rounded-md shadow-xs shrink-0 w-full ${className}`}>
      <button
        type="button"
        onClick={() => onChange('Yes')}
        className={`flex-1 flex gap-0 items-center justify-center min-h-[40px] px-[16px] py-[8px] relative shrink-0 border-r border-border-primary border-solid ${
          value === 'Yes' ? 'bg-foreground-brand-primary-alt' : 'bg-background-primary'
        }`}
      >
        <p className={`flex-1 font-body font-semibold text-sm leading-sm text-center ${
          value === 'Yes' ? 'text-text-secondary-hover' : 'text-text-secondary-700'
        }`}>Yes</p>
      </button>
      <button
        type="button"
        onClick={() => !disableNo && onChange('No')}
        disabled={disableNo}
        className={`flex-1 flex gap-0 items-center justify-center min-h-[40px] px-[16px] py-[8px] relative shrink-0 ${
          value === 'No' ? 'bg-foreground-brand-primary-alt' : 'bg-background-primary'
        } ${disableNo ? 'opacity-40 cursor-not-allowed' : ''}`}
      >
        <p className={`flex-1 font-body font-semibold text-sm leading-sm text-center ${
          value === 'No' ? 'text-text-secondary-hover' : 'text-text-secondary-700'
        }`}>No</p>
      </button>
    </div>
  )
}
